/**
 * Utility functions for managing Mapbox tilesets
 */

import { api } from '../services/api';

/**
 * Validates a tileset URL format
 * @param {string} tilesetUrl - The tileset URL to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateTilesetUrl = (tilesetUrl) => {
  if (!tilesetUrl) return false;
  
  // Check if it's a valid Mapbox tileset URL format for raster tilesets
  const tilesetPattern = /^mapbox:\/\/[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+$/;
  return tilesetPattern.test(tilesetUrl);
};

/**
 * Extracts username and tileset ID from a tileset URL
 * @param {string} tilesetUrl - The tileset URL
 * @returns {Object|null} - Object with username and tilesetId, or null if invalid
 */
export const parseTilesetUrl = (tilesetUrl) => {
  if (!validateTilesetUrl(tilesetUrl)) return null;
  
  const match = tilesetUrl.match(/^mapbox:\/\/([a-zA-Z0-9_-]+)\.([a-zA-Z0-9_-]+)$/);
  if (!match) return null;
  
  return {
    username: match[1],
    tilesetId: match[2]
  };
};

/**
 * Creates a tileset source configuration with CORS handling
 * @param {string} tilesetUrl - The tileset URL
 * @param {string} sourceId - The source ID
 * @param {string} accessToken - Mapbox access token
 * @returns {Object} - Source configuration object
 */
export const createTilesetSource = (tilesetUrl, sourceId = 'parcels-tileset', accessToken = null) => {
  // Ensure we have a valid tileset URL
  if (!tilesetUrl) {
    console.warn('createTilesetSource: tilesetUrl is required');
    return null;
  }

  // Validate the tileset URL format
  if (!validateTilesetUrl(tilesetUrl)) {
    console.warn('createTilesetSource: Invalid tileset URL format:', tilesetUrl);
    return null;
  }

  // If we have an access token, use the proper Mapbox tileset URL format
  if (accessToken && tilesetUrl.startsWith('mapbox://')) {
    const parsed = parseTilesetUrl(tilesetUrl);
    if (parsed) {
      const source = {
        id: sourceId,
        type: 'raster',
        tiles: [`https://api.mapbox.com/v4/${parsed.username}.${parsed.tilesetId}/{z}/{x}/{y}.png?access_token=${accessToken}`],
        tileSize: 256
      };
      
    //   console.log('Created tileset source with access token:', source);
      return source;
    }
  }
  
  // Fallback to direct tileset URL (may have CORS issues)
  const fallbackSource = {
    id: sourceId,
    type: 'raster',
    tiles: [tilesetUrl],
    tileSize: 256
  };
  
  console.log('Created fallback tileset source:', fallbackSource);
  return fallbackSource;
};

/**
 * Validates a tileset source configuration
 * @param {Object} source - The source configuration to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateTilesetSource = (source) => {
  if (!source) return false;
  
  const requiredProps = ['id', 'type', 'tiles', 'tileSize'];
  const hasAllProps = requiredProps.every(prop => source.hasOwnProperty(prop));
  
  if (!hasAllProps) {
    console.warn('validateTilesetSource: Missing required properties:', requiredProps.filter(prop => !source.hasOwnProperty(prop)));
    return false;
  }
  
  if (source.type !== 'raster') {
    console.warn('validateTilesetSource: Invalid type, expected "raster" but got:', source.type);
    return false;
  }
  
  if (!Array.isArray(source.tiles) || source.tiles.length === 0) {
    console.warn('validateTilesetSource: Invalid tiles array:', source.tiles);
    return false;
  }
  
  if (typeof source.tileSize !== 'number' || source.tileSize <= 0) {
    console.warn('validateTilesetSource: Invalid tileSize:', source.tileSize);
    return false;
  }
  
  return true;
};

/**
 * Creates a tileset layer configuration
 * @param {string} sourceId - The source ID
 * @param {Object} paint - Paint properties for the layer
 * @returns {Object} - Layer configuration object
 */
export const createTilesetLayer = (sourceId, paint = {}) => {
  return {
    id: `${sourceId}-layer`,
    type: 'raster',
    source: sourceId,
    paint: {
      'raster-opacity': 0.8,
      ...paint
    }
  };
};

/**
 * Gets the default tileset configuration
 * @returns {Object} - Default configuration object
 */
export const getDefaultTilesetConfig = () => {
  const config = {
    tilesetUrl: process.env.REACT_APP_TILESET_URL || 'mapbox://parcelhunter.orange_ca-parcels',
    sourceId: 'orange_ca-parcels',
    fallbackUrl: process.env.REACT_APP_FALLBACK_TILESET_URL || null
  };
  
  // Debug logging
  console.log('Default tileset config:', config);
  
  return config;
};

/**
 * Creates a CORS proxy URL for tileset access
 * @param {string} tilesetUrl - The original tileset URL
 * @param {string} proxyUrl - The CORS proxy URL (optional)
 * @returns {string} - Proxied tileset URL
 */
export const createCorsProxyUrl = (tilesetUrl, proxyUrl = null) => {
  // Use a public CORS proxy if none provided
  const defaultProxy = 'https://cors-anywhere.herokuapp.com/';
  const proxy = proxyUrl || defaultProxy;
  
  return `${proxy}${tilesetUrl}`;
};

/**
 * Gets available tileset configurations with fallbacks
 * @param {string} accessToken - Mapbox access token
 * @returns {Array} - Array of tileset configurations to try
 */
export const getTilesetConfigurations = (accessToken) => {
  const configs = [];
  
  // Primary tileset configuration
  const primaryConfig = getDefaultTilesetConfig();
  if (primaryConfig.tilesetUrl) {
    configs.push({
      ...primaryConfig,
      tilesetUrl: primaryConfig.tilesetUrl,
      accessToken
    });
  }
  
  // Fallback tileset if specified
  if (primaryConfig.fallbackUrl) {
    configs.push({
      ...primaryConfig,
      tilesetUrl: primaryConfig.fallbackUrl,
      sourceId: `${primaryConfig.sourceId}-fallback`,
      accessToken
    });
  }
  
  // Additional fallback options
  const fallbackTilesets = [
    'mapbox://mapbox.satellite',
    'mapbox://mapbox.streets',
    'mapbox://mapbox.light',
    'mapbox://mapbox.dark'
  ];
  
  fallbackTilesets.forEach((tileset, index) => {
    configs.push({
      tilesetUrl: tileset,
      sourceId: `fallback-${index}`,
      accessToken
    });
  });
  
  return configs;
};

/**
 * Generates a tileset URL for a given state and county
 * @param {string} state - State name
 * @param {string} county - County name
 * @returns {Promise<string>} - Tileset URL in the format mapbox://parcelhunter.{county_name}_{state_code}-parcels
 */
export const getTilesetUrlForJurisdiction = async (state, county) => {
  const stateCode = await api.getStateCode(state);
  const countyName = api.formatCountyForAPI(county).replace(/ /g, '_');
  return `mapbox://parcelhunter.${countyName}_${stateCode}-parcels`;
};

 